import { useEffect, useRef, useState } from 'react';
import {
  ArrowUp,
  Database,
  DatabaseBackup,
  DatabaseZap,
  Loader2,
  Microscope,
  Zap,
} from 'lucide-react';
import { Textarea } from '@/components/UI/textarea';
import { cn } from '@/lib/utils';
import { AnchoredTooltip } from '../ui/anchored-tooltip';
import {
  type AnalysisMode,
  readPersistedAnalysisMode,
  writePersistedAnalysisMode,
} from '../../utils/analysisMode';

export type { AnalysisMode };
export type DataSourceStatus = 'connected' | 'disconnected' | 'warning';

interface ChatInputProps {
  onSubmit: (query: string, analysisMode: AnalysisMode) => void;
  isLoading: boolean;
  dataSourceStatus?: DataSourceStatus;
}

const DATA_SOURCE_STATUS_COPY: Record<DataSourceStatus, string> = {
  connected: 'Connected',
  disconnected: 'Not Connected',
  warning: 'Need to Reconnect',
};

const DATA_SOURCE_STATUS_CLASSES: Record<DataSourceStatus, string> = {
  connected: 'text-emerald-700',
  disconnected: 'text-rose-700',
  warning: 'text-amber-700',
};

// Visual + copy spec for the segmented Normal / Deep toggle. Centralized
// so the tooltip + button stay aligned.
const MODE_OPTIONS: Array<{
  value: AnalysisMode;
  label: string;
  icon: typeof Zap;
  description: string;
}> = [
  {
    value: 'normal',
    label: 'Normal',
    icon: Zap,
    description: 'Faster — KPIs, charts, and descriptive insights.',
  },
  {
    value: 'deep',
    label: 'Deep',
    icon: Microscope,
    description: 'Adds prescriptive recommendations grounded in the analysis.',
  },
];

export function ChatInput({ onSubmit, isLoading, dataSourceStatus = 'disconnected' }: ChatInputProps) {
  const [query, setQuery] = useState('');
  const [analysisMode, setAnalysisModeState] = useState<AnalysisMode>(() => readPersistedAnalysisMode());
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const DataSourceIcon = dataSourceStatus === 'warning'
    ? DatabaseBackup
    : dataSourceStatus === 'connected'
      ? DatabaseZap
      : Database;
  const dataSourceStatusLabel = DATA_SOURCE_STATUS_COPY[dataSourceStatus];
  const dataSourceStatusClassName = DATA_SOURCE_STATUS_CLASSES[dataSourceStatus];

  const setAnalysisMode = (next: AnalysisMode) => {
    setAnalysisModeState(next);
    writePersistedAnalysisMode(next);
  };

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const nextHeight = Math.min(el.scrollHeight, 144);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > 144 ? 'auto' : 'hidden';
  }, [query]);

  const handleSubmit = () => {
    if (query.trim() && !isLoading) {
      onSubmit(query.trim(), analysisMode);
      setQuery('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    // Repo-2 branding pass: compact ivory composer for Smart Insights only.
    // Search "Repo-2 branding pass" to reuse or revert this styling layer later.
    <div className="shrink-0 border-t border-[#EDE6E1] bg-white px-3 pb-3 pt-2">
      <div className="mx-auto max-w-4xl">
        <div className="flex items-end gap-2 rounded-[12px] border border-[#EDE6E1] bg-white px-2 py-1.5 shadow-sm transition-all duration-200 focus-within:border-[#6E3450]/60 focus-within:shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)]">
          <div className="relative flex-1">
            <Textarea
              ref={textareaRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about MTD margin, plan-vs-actual, projection, drivers..."
              className="max-h-36 min-h-[38px] resize-none overflow-hidden border-0 bg-transparent px-1.5 py-1.5 text-[13px] leading-relaxed text-[#1F1A20] shadow-none outline-none placeholder:text-[#A8989F] focus-visible:ring-0 focus-visible:ring-offset-0"
              disabled={isLoading}
              rows={1}
            />
          </div>

          <button
            type="button"
            onClick={handleSubmit}
            disabled={!query.trim() || isLoading}
            className={cn(
              'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] transition-all duration-200',
              query.trim() && !isLoading
                ? 'bg-[#6E3450] text-white shadow-sm hover:bg-[#5A2C58]'
                : 'bg-[#F5EFE6] text-[#A8989F]'
            )}
            aria-label="Send message"
          >
            {isLoading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <ArrowUp className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-2 px-1 text-[10px] text-[#A8989F]">
          <AnchoredTooltip
            side="top"
            align="start"
            content={(
              <span className="flex min-w-[9rem] flex-col gap-1 font-display">
                <span className={cn('inline-flex items-center gap-1.5 text-[12px] font-semibold', dataSourceStatusClassName)}>
                  <DataSourceIcon className="h-3.5 w-3.5" />
                  Data Source
                </span>
                <span className="text-[11.5px] font-medium text-[#3F3338]">
                  {dataSourceStatusLabel}
                </span>
              </span>
            )}
            contentClassName="rounded-[10px] border-[#EDE6E1] bg-white px-3 py-2 text-left shadow-[0_4px_6px_-1px_rgba(31,26,32,.06),0_12px_28px_-10px_rgba(110,52,80,.18)]"
            className="cursor-default"
          >
            <span
              className={cn('inline-flex cursor-default select-none items-center gap-1.5 font-display text-[10.5px] font-semibold', dataSourceStatusClassName)}
              aria-label={`Data Source: ${dataSourceStatusLabel}`}
            >
              <DataSourceIcon className="h-3.5 w-3.5 shrink-0" />
              <span>Data Source</span>
            </span>
          </AnchoredTooltip>
          <div className="flex items-center gap-2">
            <span className="hidden font-display text-[10px] font-semibold uppercase tracking-[0.18em] text-[#A8989F] sm:inline">
              Effort
            </span>
            <div
              className="relative inline-grid auto-cols-fr grid-flow-col items-stretch rounded-full border border-[#EDE6E1] bg-[#F5EFE6] p-[3px]"
              role="radiogroup"
              aria-label="Analysis effort"
            >
              {/* Sliding indicator — animates between the two segments
                  based on the selected mode. Equal-width grid columns
                  guarantee the indicator's 50% width matches a segment
                  exactly regardless of label length. */}
              <span
                aria-hidden="true"
                className={cn(
                  'pointer-events-none absolute top-[3px] bottom-[3px] left-[3px] w-[calc(50%-3px)] rounded-full bg-white shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.08)] transition-transform duration-200 ease-out',
                  analysisMode === 'deep' ? 'translate-x-full' : 'translate-x-0'
                )}
              />
              {MODE_OPTIONS.map(({ value, label, icon: Icon, description }) => {
                const selected = analysisMode === value;
                return (
                  <AnchoredTooltip
                    key={value}
                    side="top"
                    className="flex"
                    content={(
                      <span className="flex min-w-[12rem] flex-col gap-0.5 font-display">
                        <span className="text-[12px] font-semibold text-[#1F1A20]">
                          {label} mode
                        </span>
                        <span className="text-[11.5px] text-[#5A4E54]">
                          {description}
                        </span>
                      </span>
                    )}
                    contentClassName="rounded-[10px] border-[#EDE6E1] bg-white px-3 py-2 text-left shadow-[0_4px_6px_-1px_rgba(31,26,32,.06),0_12px_28px_-10px_rgba(110,52,80,.18)]"
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      aria-label={`${label} mode — ${description}`}
                      onClick={(e) => {
                        setAnalysisMode(value);
                        // Drop focus so the tooltip's group-focus-within
                        // releases and the popover closes immediately
                        // after click instead of pinning open until the
                        // pointer leaves.
                        e.currentTarget.blur();
                      }}
                      disabled={isLoading}
                      className={cn(
                        'relative z-10 inline-flex h-6 w-full items-center justify-center gap-1.5 rounded-full px-3 font-display text-[10.5px] font-semibold uppercase tracking-[0.16em] transition-colors duration-200 ease-out disabled:cursor-not-allowed disabled:opacity-50',
                        selected
                          ? 'text-[#6E3450]'
                          : 'text-[#7B6B72] hover:text-[#1F1A20]'
                      )}
                    >
                      <Icon className="h-3 w-3" strokeWidth={2.5} />
                      <span>{label}</span>
                    </button>
                  </AnchoredTooltip>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
